import React, { Component } from "react";
import PropTypes from "prop-types";
import classNames from "classnames";
import MediaQuery from "react-responsive";

import { Part, PartsCollection } from "models/Part";

import MonitorBlock from "./MonitorBlock";
import AccessorySelection from "./AccessorySelection";

import "./Configurator.css";
import "./AccessorySequence.css";
import connect from "react-redux/es/connect/connect";

const getAccessoryGroups = (partsList, component) => {
  const flattenedParts = partsList.reduce(
    (acc, part) => (Array.isArray(part) ? [...acc, ...part] : [...acc, part]),
    [],
  );
  const uniqueGroups = [
    ...new Set(
      flattenedParts
        .filter((part) => typeof part.group !== "undefined")
        .map((part) => part.group),
    ),
  ];
  return uniqueGroups.length > 0 ? uniqueGroups[0] : component;
};

const getAccessorySequence = (accessoryChoices) =>
  Array.from(accessoryChoices.keys());

// TODO: Change this to PartSequence. Replace 'Accessory' with 'Part'.
// Keep using 'accessory' for the part 'type'
class AccessorySequence extends Component {
  // number of columns to use in displaying all accessory dropdowns
  static totalDisplayColumns = 0;

  componentDidUpdate(prevProps) {
    const { accessoryChoices, onAccessoryChoicesChanged } = this.props;

    // only update if we need to solve for new accessory choices
    // set 'choice' for accessories where possible
    if (accessoryChoices && !prevProps.accessoryChoices) {
      const newAccessoryChoices = this.solveAccessoryChoices(
        new Map(accessoryChoices),
        accessoryChoices.keys().next().value,
      );

      // pre-calculate total number of display columns for all accessories
      AccessorySequence.totalDisplayColumns = Array.from(
        newAccessoryChoices.values(),
      ).reduce((acc, value) => acc + parseInt(value.flex, 10), 0);

      // the choices are ready to be renedered
      onAccessoryChoicesChanged(newAccessoryChoices);
    }
  }

  gatherAccessoryOptions(accessoryChoices, id) {
    // TODO - first item in sequence should not also be 'accessory', but something like 'pod'
    const choice = accessoryChoices.get(id);
    if (choice.optionsLabels) {
      return choice.optionsLabels;
    }

    const { partsCollection, filterChoices } = this.props;

    // accessory is first lower drop down
    if (id === "accessory") {
      // we know these are parts
      return partsCollection.findAccessoryOptions(
        getAccessorySequence(accessoryChoices),
      );
    }

    // following drop-downs need the accessory part...
    const accessoryChoice = accessoryChoices.get("accessory");
    const accessoryPart = accessoryChoice ? accessoryChoice.choice[0] : null;
    const kitChoice = accessoryChoices.get("kit");
    const kitPart = kitChoice ? kitChoice.choice[0] : null;

    // kits can float anywhere
    if (id.indexOf("kit") === 0) {
      // select all remaining terminal parts
      return partsCollection.findKitOptions(accessoryPart);
    }

    // adapters, extension cables, intermediate parts, etc.
    if (id === "intermediate-cable") {
      // select all possible options for extension cables
      return partsCollection.findConnectorOptions(accessoryPart, {
        patientParameter: filterChoices.get("patientParameter").choice,
        patientCategory: filterChoices.get("patientCategory").choice,
      });
    }

    // down to the terminal parts
    // (use indexOf to handle 'terminal/', 'terminal/', case
    if (id.indexOf("terminal") === 0) {
      // if 'kitPart' is not actually a kit but a solo part, we don't want pairings here
      // (etco2 > sidestream)
      if (kitPart && kitPart.type !== "kit") {
        return [];
      }

      // cablePart can also be found if it's bundled with the accessory
      const cableChoice = accessoryChoices.get("intermediate-cable");
      const cablePart = cableChoice
        ? cableChoice.choice[0]
        : accessoryChoice &&
          accessoryChoice.includedParts.find(
            (part) => part.type === "intermediate-cable",
          );

      // select all remaining terminal parts
      // console.log('why is this called twice? TODO: maybe gatherAccessoryOptions should be memoized', accessoryPart, kitPart);
      return partsCollection.findTerminalOptions(
        accessoryPart || kitPart,
        cablePart,
        accessoryChoices.get(id).parameterOptionFilter,
      );
    }

    return [];
  }

  saveAccessoryChoice(accessoryChoices, id, option) {
    const accessoryChoice = accessoryChoices.get(id);
    accessoryChoice.choice = Array.isArray(option) ? option : [option];

    const { partsCollection, filterChoices } = this.props;
    const { choice: chosenParameterOption } =
      filterChoices.get("parameterOption");
    accessoryChoice.includedParts = partsCollection.collectPartsFromPartNumbers(
      accessoryChoice.choice.reduce(
        (acc, part) => [
          ...acc,
          ...(part instanceof Part
            ? partsCollection.bundleIncludedParts(part, chosenParameterOption)
            : []),
        ],
        [],
      ),
    );

    // special case for ecg/terminal and BIS
    // Looks for adapaters on the terminal parts, but adapters are actually paired with the 'pod'
    const { choice: chosenPatientParameter } =
      filterChoices.get("patientParameter");
    let adapters;
    const podChoice = accessoryChoices.get("accessory");
    if (chosenPatientParameter === "ecg") {
      if (id.indexOf("terminal") === 0 && podChoice) {
        adapters = partsCollection.findCompatibleAdapters(
          podChoice.choice[0],
          accessoryChoice.choice[0],
        );
      }
    } else if (chosenPatientParameter === "bis") {
      if (podChoice) {
        adapters = partsCollection.findCompatibleAdapters(podChoice.choice[0]);
      }
    } else {
      // general adapter case
      // used by:
      // Vista 120S-C / Vista 120-C; etCO2 > Sidestream
      // Vista 120S-xyz; Temp
      if (id.indexOf("terminal") === 0) {
        adapters = partsCollection.findCompatibleAdapters(
          (podChoice ? podChoice : accessoryChoice).choice[0],
        );
      }
    }

    // add adapters if there are any
    if (adapters && adapters.length > 0) {
      accessoryChoice.includedParts = [
        ...accessoryChoice.includedParts,
        ...adapters,
      ];
    }

    // sort included parts based on special cases
    accessoryChoice.includedParts.sort((a, b) => {
      if (a.group === "connector") return -1;
      if (b.group === "connector") return 1;
      if (a.group === "extension-cable") return -1;
      if (b.group === "extension-cable") return 1;
      if (a.group === "y-cable") return -1;
      if (b.group === "y-cable") return 1;
      if (a.type === "adapter") return -1;
      if (b.type === "adapter") return 1;
      return 0;
    });

    // determine whether to show comment
    accessoryChoice.caption = accessoryChoice.choice.forEach((part) => {
      if (part instanceof Part) {
        part.parseComment(filterChoices);
      }
    });

    console.log("parts", id, partsCollection);
    console.log(
      "accessories",
      partsCollection.filteredList.filter((part) => part.type === "accessory"),
    );
    console.log(
      "cables",
      partsCollection.filteredList.filter(
        (part) => part.type === "intermediate-cable",
      ),
    );
    console.log(
      "terminals",
      partsCollection.filteredList.filter((part) => part.type === "terminal"),
    );
    console.log(
      "kits",
      partsCollection.filteredList.filter((part) => part.type === "kit"),
    );
    console.log("-------------------------------------------");
    return accessoryChoices;
  }

  solveAccessoryChoices(accessoryChoices, id) {
    if (!id) {
      return accessoryChoices;
    }

    const accessoryChoice = accessoryChoices.get(id);
    const optionsLabels = this.gatherAccessoryOptions(accessoryChoices, id);
    accessoryChoice.optionsLabels = optionsLabels;
    accessoryChoice.solved = true;
    if (optionsLabels.length <= 1) {
      this.saveAccessoryChoice(
        accessoryChoices,
        id,
        optionsLabels.length === 1 ? optionsLabels[0] : "n/a",
      );
      return this.solveAccessoryChoices(
        accessoryChoices,
        accessoryChoice.nextId,
      );
    }
    return accessoryChoices;
  }

  onSelectAccessory = (id, option) => {
    const { accessoryChoices } = this.props;

    // fill in choice, add parts 'bundle' if on last component of sequence
    const newAccessoryChoices = new Map(accessoryChoices);
    if (option === "") {
      // clear accessories from id forward
      const entriesIt = newAccessoryChoices.entries();
      let entry = entriesIt.next();
      let clear = false;
      while (!entry.done) {
        if (clear || entry.value[0] === id) {
          const accessoryChoice = entry.value[1];
          accessoryChoice.choice = [];
          accessoryChoice.includedParts = [];
          accessoryChoice.optionsLabels = null;
          clear = true;
        }
        entry = entriesIt.next();
      }
    } else {
      this.saveAccessoryChoice(newAccessoryChoices, id, option);
      this.solveAccessoryChoices(
        newAccessoryChoices,
        accessoryChoices.get(id).nextId,
      );
    }

    const { onAccessoryChoicesChanged } = this.props;
    onAccessoryChoicesChanged(newAccessoryChoices);
  };

  onDropAccessory = (id) => {
    // clear the value of this dropdown and all subsequent choices
    this.onSelectAccessory(id, "");
  };

  renderAccessorySelection(id) {
    if (!this.isAccessoryAreaActive()) {
      return null;
    }
    const { accessoryChoices } = this.props;
    const accessoryChoice = accessoryChoices.get(id); // NOTE: this obviously doesn't work for the 'terminal', 'terminal' case
    if (
      accessoryChoice.prevId &&
      accessoryChoices.get(accessoryChoice.prevId).choice.length === 0
    ) {
      return null;
    }
    if (!accessoryChoice.solved || accessoryChoice.choice[0] === "n/a") {
      return null;
    }
    const getNumBootColumns = (flex) => [
      Math.floor((24 * flex) / AccessorySequence.totalDisplayColumns),
      flex / AccessorySequence.totalDisplayColumns > 0.5 ? 24 : 12,
    ];
    const numBootColumns = getNumBootColumns(accessoryChoice.flex);
    const options = this.gatherAccessoryOptions(accessoryChoices, id);
    const group = getAccessoryGroups(options, id);

    return (
      <div
        key={id}
        className={`col-md-${numBootColumns[0]} col-sm-${numBootColumns[1]}`}
      >
        <AccessorySelection
          id={id}
          placeholder={group}
          choice={accessoryChoice.choice}
          bundle={accessoryChoice.includedParts}
          options={options}
          onSelect={this.onSelectAccessory}
          onDrop={this.onDropAccessory}
        />
      </div>
    );
  }

  isAccessoryAreaActive() {
    const { accessoryChoices } = this.props;
    return accessoryChoices !== null;
  }

  render() {
    const {
      monitor,
      accessoryChoices,
      filterChoices,
      partsCollection,
      getString,
    } = this.props;
    const bottomBlockStyle = {
      backgroundColor: this.isAccessoryAreaActive() ? "#ecf0f3" : "#ffffff",
    };
    const renderChoices = () =>
      accessoryChoices &&
      getAccessorySequence(accessoryChoices).map((id) =>
        this.renderAccessorySelection(id),
      );

    // gather some connection options to be used to determine the monitor image with
    // a port or other connection highlighted.
    const connectionOptions = [];
    if (accessoryChoices) {
      if (filterChoices) {
        connectionOptions.push(filterChoices.get("patientParameter").choice);
      }
      const values = Array.from(accessoryChoices.values());
      if (values.length > 0) {
        // first look at the selected part..
        const accessoryChoice = values[0];
        if (accessoryChoice.choice.length > 0) {
          if (accessoryChoice.choice[0].type === "kit") {
            connectionOptions.unshift(
              ...accessoryChoice.includedParts.map(
                (part) => part.monitorConnection || part.group,
              ),
            );
          } else {
            // use the group of the part this choice is paired with
            connectionOptions.unshift(
              ...partsCollection.findMonitorConnectionOptions(
                accessoryChoice.choice,
              ),
            );
          }
        } else if (accessoryChoice.optionsLabels) {
          // if no part is selected yet, look at all the options and see if there's a consensus
          // all connections must be the same to use it now
          const foundConnections = partsCollection.findMonitorConnectionOptions(
            accessoryChoice.optionsLabels,
          );
          if (
            foundConnections.every(
              (connection, idx, arr) => idx === 0 || connection === arr[0],
            )
          ) {
            console.log("foundConnections", foundConnections);
            connectionOptions.unshift(...foundConnections);
          }
        }
      }
    }
    //console.log('connection options', connectionOptions);

    return (
      <div className="bottom-block" style={bottomBlockStyle}>
        <div className="row">
          <div className="col-md-18 col-sm-12 col-md-offset-6 col-sm-offset-12">
            <h2
              className={classNames("block-head", {
                "block-hide": !this.isAccessoryAreaActive(),
              })}
            >
              <span className="number">3</span>
              {getString("accessories", "general")}
            </h2>
          </div>
        </div>
        <div className="row">
          <div className="col-md-6 col-sm-12">
            <MonitorBlock
              connectionOptions={connectionOptions}
              monitor={monitor}
            />
          </div>
          <MediaQuery query="(min-width: 992px)">
            <div className="col-md-18 col-sm-12">
              <div className="row">{renderChoices()}</div>
            </div>
          </MediaQuery>
          <MediaQuery query="(max-width: 991px)">{renderChoices()}</MediaQuery>
        </div>
      </div>
    );
  }
}

AccessorySequence.defaultProps = {
  filterChoices: null,
  accessoryChoices: null,
};
AccessorySequence.propTypes = {
  onAccessoryChoicesChanged: PropTypes.func.isRequired,
  accessoryChoices: PropTypes.instanceOf(Map),
  filterChoices: PropTypes.instanceOf(Map),
  monitor: PropTypes.shape().isRequired,
  partsCollection: PropTypes.instanceOf(PartsCollection).isRequired,
  getString: PropTypes.func.isRequired,
};

export default connect((state) => ({
  getString: state.strings.getString,
}))(AccessorySequence);
